'use client';
import React, { useState, useMemo } from 'react';
import { Search, X, Clock, ArrowUpRight, Lock, Eye, CheckCircle, AlertCircle, SlidersHorizontal, RefreshCw, Star } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';



// ─── Types ────────────────────────────────────────────────────────────────────

type RiskLevel = 'Low' | 'Medium' | 'High';
type EligibilityStatus = 'eligible' | 'not-eligible' | 'pending-kyc' | 'amount-restricted';

interface Campaign {
  id: string;
  name: string;
  issuer: string;
  category: string;
  riskLevel: RiskLevel;
  target: number;
  raised: number;
  funded: number;
  minInvestment: number;
  irr: string;
  tenor: string;
  daysLeft: number;
  eligibility: EligibilityStatus;
  eligibilityNote?: string;
  image: string;
  imageAlt: string;
  imageTint: string;
  featured?: boolean;
  jurisdiction: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const allCampaigns: Campaign[] = [
  {
    id: 'camp-001', name: 'Meridian Logistics Hub — Series B', issuer: 'Meridian Infrastructure PLC',
    category: 'Infrastructure', riskLevel: 'Medium', target: 12500000, raised: 9875000, funded: 79,
    minInvestment: 50000, irr: '14.2%', tenor: '5 yrs', daysLeft: 23,
    eligibility: 'eligible', jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Meridian Logistics Hub — modern warehouse and logistics facility in Jebel Ali Free Zone',
    imageTint: 'from-blue-900/60 to-blue-700/20', featured: true,
  },
  {
    id: 'camp-002', name: 'Ashford Commercial REIT II', issuer: 'Ashford Capital Partners',
    category: 'Real Estate', riskLevel: 'Low', target: 25000000, raised: 14200000, funded: 57,
    minInvestment: 100000, irr: '11.8%', tenor: '7 yrs', daysLeft: 41,
    eligibility: 'eligible', jurisdiction: 'UAE (ADGM)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Ashford Commercial REIT II — premium commercial property portfolio across GCC',
    imageTint: 'from-amber-900/60 to-amber-700/20',
  },
  {
    id: 'camp-003', name: 'ClearPath Clean Energy Fund III', issuer: 'ClearPath Renewables Ltd',
    category: 'Clean Energy', riskLevel: 'Low', target: 8000000, raised: 7840000, funded: 98,
    minInvestment: 25000, irr: '12.6%', tenor: '8 yrs', daysLeft: 7,
    eligibility: 'not-eligible', eligibilityNote: 'Fund nearly full — below minimum allocation threshold',
    jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'ClearPath Clean Energy Fund III — solar and wind renewable energy infrastructure',
    imageTint: 'from-green-900/60 to-green-700/20',
  },
  {
    id: 'camp-004', name: 'Vantage Private Credit Fund I', issuer: 'Vantage Capital Management',
    category: 'Debt / Credit', riskLevel: 'Medium', target: 15000000, raised: 4500000, funded: 30,
    minInvestment: 75000, irr: '9.4%', tenor: '3 yrs', daysLeft: 58,
    eligibility: 'eligible', jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Vantage Private Credit Fund I — institutional private credit and SME lending portfolio',
    imageTint: 'from-purple-900/60 to-purple-700/20',
  },
  {
    id: 'camp-005', name: 'Solaris Renewable Energy Fund III', issuer: 'Solaris Asset Management DIFC',
    category: 'Clean Energy', riskLevel: 'Medium', target: 25000000, raised: 0, funded: 0,
    minInvestment: 50000, irr: '13.1%', tenor: '7 yrs', daysLeft: 72,
    eligibility: 'pending-kyc', eligibilityNote: 'Annual suitability re-assessment required',
    jurisdiction: 'UAE (ADGM)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Solaris Renewable Energy Fund III — utility-scale solar projects across MENA region',
    imageTint: 'from-yellow-900/60 to-yellow-700/20',
  },
  {
    id: 'camp-006', name: 'Harborview Private Credit Fund', issuer: 'Harborview Capital DIFC',
    category: 'Debt / Credit', riskLevel: 'High', target: 40000000, raised: 0, funded: 0,
    minInvestment: 100000, irr: '15.5%', tenor: '5 yrs', daysLeft: 90,
    eligibility: 'amount-restricted', eligibilityNote: 'Min. investment $100K — exceeds your current allocation limit',
    jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Harborview Private Credit Fund — senior secured SME lending across GCC markets',
    imageTint: 'from-red-900/60 to-red-700/20',
  },
  {
    id: 'camp-007', name: 'Nexus PropTech Growth Capital', issuer: 'Nexus Ventures MENA',
    category: 'Technology', riskLevel: 'High', target: 5000000, raised: 1200000, funded: 24,
    minInvestment: 5000, irr: '18.0%', tenor: '3 yrs', daysLeft: 45,
    eligibility: 'eligible', jurisdiction: 'UAE (Mainland)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Nexus PropTech Growth Capital — B2B PropTech SaaS platform for GCC real estate developers',
    imageTint: 'from-cyan-900/60 to-cyan-700/20',
  },
  {
    id: 'camp-008', name: 'GCC Healthcare REIT I', issuer: 'MedCapital Partners',
    category: 'Healthcare', riskLevel: 'Low', target: 18000000, raised: 9000000, funded: 50,
    minInvestment: 30000, irr: '10.2%', tenor: '10 yrs', daysLeft: 35,
    eligibility: 'eligible', jurisdiction: 'UAE (ADGM)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'GCC Healthcare REIT I — diversified healthcare real estate investment trust across Gulf region',
    imageTint: 'from-teal-900/60 to-teal-700/20',
  },
];

const categories = ['All', 'Infrastructure', 'Real Estate', 'Clean Energy', 'Debt / Credit', 'Technology', 'Healthcare'];
const riskLevels: RiskLevel[] = ['Low', 'Medium', 'High'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const riskConfig: Record<RiskLevel, { color: string; bg: string; variant: any }> = {
  Low: { color: 'text-positive', bg: 'bg-positive/10 border-positive/20', variant: 'low' },
  Medium: { color: 'text-warning', bg: 'bg-warning/10 border-warning/20', variant: 'medium' },
  High: { color: 'text-critical', bg: 'bg-critical/10 border-critical/20', variant: 'high' },
};

const eligibilityConfig: Record<EligibilityStatus, { icon: React.ElementType; color: string; label: string; canInvest: boolean }> = {
  eligible: { icon: CheckCircle, color: 'text-positive', label: 'Eligible', canInvest: true },
  'not-eligible': { icon: X, color: 'text-critical', label: 'Not Eligible', canInvest: false },
  'pending-kyc': { icon: AlertCircle, color: 'text-warning', label: 'KYC Required', canInvest: false },
  'amount-restricted': { icon: Lock, color: 'text-warning', label: 'Amount Restricted', canInvest: false },
};

function FundingBar({ funded, riskLevel }: { funded: number; riskLevel: RiskLevel }) {
  const fillColor = funded >= 90 ? 'bg-gradient-to-r from-positive to-accent' :
    funded >= 50 ? 'bg-gradient-to-r from-primary to-accent': 'bg-gradient-to-r from-surface-4 to-primary';
  return (
    <div className="funding-thermometer">
      <div className={`funding-thermometer-fill ${fillColor}`} style={{ width: `${funded}%` }} />
    </div>
  );
}

// ─── Range Slider ─────────────────────────────────────────────────────────────

function RangeSlider({ min, max, value, onChange, label }: {
  min: number; max: number; value: [number, number];
  onChange: (v: [number, number]) => void; label: string;
}) {
  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <span className="text-xs font-mono-data text-foreground">{value[0]}% – {value[1]}%</span>
      </div>
      <div className="relative h-5 flex items-center">
        <div className="absolute w-full h-1.5 bg-surface-4 rounded-full" />
        <div
          className="absolute h-1.5 bg-gradient-to-r from-primary to-accent rounded-full"
          style={{ left: `${pct(value[0])}%`, right: `${100 - pct(value[1])}%` }}
        />
        <input
          type="range" min={min} max={max} value={value[0]}
          onChange={(e) => { const v = Number(e.target.value); if (v < value[1]) onChange([v, value[1]]); }}
          className="absolute w-full h-1.5 opacity-0 cursor-pointer"
          style={{ zIndex: 3 }}
        />
        <input
          type="range" min={min} max={max} value={value[1]}
          onChange={(e) => { const v = Number(e.target.value); if (v > value[0]) onChange([value[0], v]); }}
          className="absolute w-full h-1.5 opacity-0 cursor-pointer"
          style={{ zIndex: 4 }}
        />
        {/* Thumb indicators */}
        <div className="absolute w-3.5 h-3.5 bg-primary rounded-full border-2 border-background shadow-lg pointer-events-none"
          style={{ left: `calc(${pct(value[0])}% - 7px)`, zIndex: 5 }} />
        <div className="absolute w-3.5 h-3.5 bg-accent rounded-full border-2 border-background shadow-lg pointer-events-none"
          style={{ left: `calc(${pct(value[1])}% - 7px)`, zIndex: 5 }} />
      </div>
    </div>
  );
}

// ─── Campaign Card ────────────────────────────────────────────────────────────

function CampaignCard({ campaign }: { campaign: Campaign }) {
  const eligCfg = eligibilityConfig[campaign.eligibility];
  const riskCfg = riskConfig[campaign.riskLevel];
  const EligIcon = eligCfg.icon;

  return (
    <Link href={`/campaign-detail/${campaign.id}`} className={`glass-card rounded-xl overflow-hidden card-hover flex flex-col ${!eligCfg.canInvest ? 'opacity-80' : ''} cursor-pointer`}>
      {/* Hero image */}
      <div className="relative h-44 w-full overflow-hidden flex-shrink-0">
        <Image src={campaign.image} alt={campaign.imageAlt} fill className="object-cover" sizes="(max-width: 768px) 100vw, 33vw" />
        <div className={`absolute inset-0 bg-gradient-to-br ${campaign.imageTint}`} />
        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs bg-black/50 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-medium">{campaign.category}</span>
            {campaign.featured && (
              <span className="text-xs bg-primary/80 text-white px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                <Star size={9} /> Featured
              </span>
            )}
          </div>
          {/* Risk badge */}
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${riskCfg.bg} ${riskCfg.color}`}>
            {campaign.riskLevel} Risk
          </span>
        </div>
        {/* Bottom: days left + eligibility */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs text-white">
            <Clock size={11} />
            <span className="font-mono-data">{campaign.daysLeft}d left</span>
          </div>
          <div className={`flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-medium ${eligCfg.color}`}>
            <EligIcon size={11} />
            {eligCfg.label}
          </div>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="mb-3">
          <p className="font-semibold text-foreground text-sm leading-snug">{campaign.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{campaign.issuer}</p>
          <p className="text-xs text-muted-foreground/70 mt-0.5">{campaign.jurisdiction}</p>
        </div>

        {/* Eligibility note */}
        {campaign.eligibilityNote && !eligCfg.canInvest && (
          <div className="mb-3 flex items-start gap-1.5 bg-warning/10 border border-warning/20 rounded-lg px-2.5 py-2">
            <AlertCircle size={12} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-warning leading-relaxed">{campaign.eligibilityNote}</p>
          </div>
        )}

        {/* Funding progress */}
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-muted-foreground">Funding Progress</span>
            <span className="font-mono-data font-semibold text-foreground">{campaign.funded}%</span>
          </div>
          <FundingBar funded={campaign.funded} riskLevel={campaign.riskLevel} />
          <div className="flex justify-between text-xs mt-1 text-muted-foreground">
            <span className="font-mono-data">${(campaign.raised / 1_000_000).toFixed(2)}M raised</span>
            <span className="font-mono-data">of ${(campaign.target / 1_000_000).toFixed(1)}M</span>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { label: 'Target IRR', value: campaign.irr },
            { label: 'Tenor', value: campaign.tenor },
            { label: 'Min. Invest', value: `$${campaign.minInvestment >= 1000 ? `${campaign.minInvestment / 1000}K` : campaign.minInvestment}` },
          ].map((m) => (
            <div key={m.label} className="bg-surface-3 rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">{m.label}</p>
              <p className="font-mono-data font-semibold text-foreground text-sm mt-0.5">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 mt-auto">
          <span className="btn-ghost text-xs gap-1"><Eye size={12} /> View</span>
          {eligCfg.canInvest ? (
            <span className="btn-primary text-xs gap-1"><ArrowUpRight size={12} /> Invest</span>
          ) : (
            <span className="btn-secondary text-xs gap-1 opacity-50">
              <Lock size={12} /> Restricted
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function CampaignDiscovery() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedRisks, setSelectedRisks] = useState<RiskLevel[]>([]);
  const [fundingRange, setFundingRange] = useState<[number, number]>([0, 100]);
  const [eligibilityFilter, setEligibilityFilter] = useState<'all' | 'eligible-only'>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState<'funded' | 'irr' | 'daysLeft' | 'target'>('funded');

  const toggleRisk = (r: RiskLevel) =>
    setSelectedRisks((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const filtered = useMemo(() => {
    return allCampaigns
      .filter((c) => {
        if (search && !c.name.toLowerCase().includes(search.toLowerCase()) &&
          !c.issuer.toLowerCase().includes(search.toLowerCase()) &&
          !c.category.toLowerCase().includes(search.toLowerCase())) return false;
        if (selectedCategory !== 'All' && c.category !== selectedCategory) return false;
        if (selectedRisks.length > 0 && !selectedRisks.includes(c.riskLevel)) return false;
        if (c.funded < fundingRange[0] || c.funded > fundingRange[1]) return false;
        if (eligibilityFilter === 'eligible-only' && c.eligibility !== 'eligible') return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'funded') return b.funded - a.funded;
        if (sortBy === 'irr') return parseFloat(b.irr) - parseFloat(a.irr);
        if (sortBy === 'daysLeft') return a.daysLeft - b.daysLeft;
        if (sortBy === 'target') return b.target - a.target;
        return 0;
      });
  }, [search, selectedCategory, selectedRisks, fundingRange, eligibilityFilter, sortBy]);

  const activeFilterCount = [
    selectedCategory !== 'All',
    selectedRisks.length > 0,
    fundingRange[0] > 0 || fundingRange[1] < 100,
    eligibilityFilter !== 'all',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('All');
    setSelectedRisks([]);
    setFundingRange([0, 100]);
    setEligibilityFilter('all');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Campaign Discovery</h1>
          <p className="text-sm text-muted-foreground mt-1">Browse {allCampaigns.length} active investment opportunities matching your profile</p>
        </div>
        <button className="btn-secondary text-xs gap-1.5">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="glass-card rounded-xl p-4 space-y-4">
        {/* Top row: search + controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns, issuers, categories…"
              className="input-field pl-9 text-sm"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={13} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary text-xs gap-1.5 flex-shrink-0 ${showFilters ? 'border-primary/40 text-primary' : ''}`}
          >
            <SlidersHorizontal size={13} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary/20 text-primary text-xs font-mono-data px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
            )}
          </button>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-muted-foreground">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="input-field text-xs py-1.5 w-auto"
            >
              <option value="funded">Funding %</option>
              <option value="irr">Target IRR</option>
              <option value="daysLeft">Closing Soon</option>
              <option value="target">Deal Size</option>
            </select>
          </div>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="border-t border-border pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Category */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Category</label>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                      selectedCategory === cat
                        ? 'bg-primary/20 text-primary border-primary/40' :'bg-surface-3 text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Risk Level */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Risk Level</label>
              <div className="flex gap-2">
                {riskLevels.map((r) => {
                  const cfg = riskConfig[r];
                  const active = selectedRisks.includes(r);
                  return (
                    <button
                      key={r}
                      onClick={() => toggleRisk(r)}
                      className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${
                        active ? `${cfg.bg} ${cfg.color}` : 'bg-surface-3 text-muted-foreground border-border hover:border-primary/30'
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Funding Range Slider */}
            <div className="space-y-2">
              <RangeSlider
                min={0} max={100}
                value={fundingRange}
                onChange={setFundingRange}
                label="Funding Progress Range"
              />
            </div>

            {/* Eligibility */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Eligibility</label>
              <div className="flex gap-2">
                {[
                  { key: 'all', label: 'All Campaigns' },
                  { key: 'eligible-only', label: 'Eligible Only' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setEligibilityFilter(opt.key as typeof eligibilityFilter)}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                      eligibilityFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/40' :'bg-surface-3 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {selectedCategory !== 'All' && (
              <span className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/20">
                {selectedCategory} <button onClick={() => setSelectedCategory('All')}><X size={10} /></button>
              </span>
            )}
            {selectedRisks.map((r) => (
              <span key={r} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${riskConfig[r].bg} ${riskConfig[r].color}`}>
                {r} Risk <button onClick={() => toggleRisk(r)}><X size={10} /></button>
              </span>
            ))}
            {(fundingRange[0] > 0 || fundingRange[1] < 100) && (
              <span className="flex items-center gap-1 text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full border border-accent/20">
                Funded: {fundingRange[0]}–{fundingRange[1]}% <button onClick={() => setFundingRange([0, 100])}><X size={10} /></button>
              </span>
            )}
            {eligibilityFilter !== 'all' && (
              <span className="flex items-center gap-1 text-xs bg-positive/10 text-positive px-2 py-0.5 rounded-full border border-positive/20">
                Eligible Only <button onClick={() => setEligibilityFilter('all')}><X size={10} /></button>
              </span>
            )}
            <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground underline ml-1">Clear all</button>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of {allCampaigns.length} campaigns
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle size={12} className="text-positive" />
          <span>{filtered.filter(c => c.eligibility === 'eligible').length} eligible for your profile</span>
        </div>
      </div>

      {/* Campaign grid */}
      {filtered.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <Search size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold text-foreground">No campaigns match your filters</p>
          <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filter criteria</p>
          <button onClick={clearFilters} className="btn-primary text-sm mt-4">Clear Filters</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((campaign) => (
            <CampaignCard key={campaign.id} campaign={campaign} />
          ))}
        </div>
      )}
    </div>
  );
}
